import React, {useEffect, useState} from "react";

import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";

import _ from "lodash";
import {handleApiException, useHttp} from "../../../services/HttpService";
import {useForm} from "../../../utils/FormUtils";
import {getAnforderungenSearchEndpoint} from "../../../services/AnforderungenService";
import {changeFieldValueIfPresent} from "../../../utils/ObjectUtils";
import {toBase64QueryParam} from "../../../utils/URLUtils";
import EAFieldset from "../../EAFieldset";
import EAButtonBar from "../../EAButtonBar";
import EAButton from "../../EAButton";
import EATable from "../../EATable";
import {createSelectRowComponentColumn} from "../../../utils/ColumnUtils";

/**
 * Attribute in AnforderungFilter
 */
const attr = {
    abwasseranlage: "abwasseranlage",
    messstelle: "messstelle", //messstelle überschreibt abwasseranlage
    parameter: "parameter",
    anfArt: "anforderungArt",

    stichtag: "stichtag", //stichtag überschreibt zeitraum
    gueltigAb: "gueltigAb",
    gueltigBis: "gueltigBis",
};
/**
 * Optionen für zeitliche RadioGroup
 */
const option = {
    stichtag: "stichtag",
    zeitraum: "zeitraum",
    alle: "alle",
};

const defaultFilter = {
    stichtag: new Date()
}

/**
 *  Komponente für die Auswertungen von Anforderungen
 * @param betriebsId
 * @param anforderungArt
 * @param gridColumns
 * @param visible
 * @param anlagenOpts
 * @param messstellenOpts
 * @param parameterOpts
 * @param onSelectionChange Callback, das beim Ändern der ausgewählten Elemente aufgerufen wird.
 *                          Wird mit den neu selektierten Werten aufgerufen.
 * @returns {null|JSX.Element}
 * @constructor
 */
function AnforderungenSuche({
                                betriebsId,
                                anforderungArt,
                                gridColumns,
                                visible,
                                anlagenOpts,
                                messstellenOpts,
                                parameterOpts,
                                onSelectionChange
                            }) {
    const id = "anforderungensuche-" + anforderungArt;

    const [selectedItems, setSelectedItems] = useState([]);
    const [data, setData] = useState([]);
    const [zeitOption, setZeitOption] = useState(option.stichtag);
    const {values: filter, setValues: setFilter, formFactory} = useForm({initialValues: defaultFilter});
    const [{
        data: anforderungen,
        loading: anforderungenLoading
    }, fetchAnforderungen] = useHttp(getAnforderungenSearchEndpoint(betriebsId, ""), {manual: true,});

    const handleSelectionChange = (items) => {
        onSelectionChange(items, data);
        setSelectedItems(items);
    }

    const columnsSlize = gridColumns.slice();
    if (gridColumns) {
        let selectionColumn = createSelectRowComponentColumn(id, "id", data ? data : [], selectedItems, handleSelectionChange);
        columnsSlize.unshift(selectionColumn);
    }

    // Falls keine IDs vergeben sind (z.B. bei berechneten Slices) werden synthetische IDs hinzugefügt
    useEffect(() => {
        if (!anforderungenLoading && anforderungen && anforderungen.length > 0) {
            for (let i = 0; i < anforderungen.length; i++) {
                const anforderungenSlice = anforderungen.slice();
                anforderungenSlice[i].id = anforderungenSlice[i].id ? anforderungenSlice[i].id : i;
                setData(anforderungenSlice);
            }
        } else if (!anforderungenLoading && anforderungen && anforderungen.length === 0) {
            setData(anforderungen);
        }
    }, [anforderungen, anforderungenLoading, setData]);

    /**
     * Setzt die Datumsfelder zurück wenn Option "alle Zeiträume" ausgewählt
     */
    useEffect(() => {
        if (zeitOption !== option.stichtag && filter[attr.stichtag] !== null) {
            setFilter({...filter, [attr.stichtag]: null});
        }
        if (zeitOption !== option.zeitraum && (filter[attr.gueltigAb] !== null || filter[attr.gueltigBis] !== null)) {
            setFilter({...filter, [attr.gueltigAb]: null, [attr.gueltigBis]: null});
        }
    }, [zeitOption, filter, setFilter]);

    /**
     * Startet die Suche nach Anforderungen anhand des States "filter"
     */
    function reFetch() {
        handleSelectionChange([]);

        const filterToSend = _.cloneDeep(filter);
        changeFieldValueIfPresent(filterToSend, attr.abwasseranlage, "id");
        changeFieldValueIfPresent(filterToSend, attr.messstelle, "id");
        changeFieldValueIfPresent(filterToSend, attr.parameter, "parameterNr");

        filterToSend[attr.anfArt] = anforderungArt;
        const q = filterToSend ? "q=" + toBase64QueryParam(filterToSend) : "";

        fetchAnforderungen(getAnforderungenSearchEndpoint(betriebsId, q)).catch((e) =>
            handleApiException(e, "Error fetching Anforderungen")
        );
    }

    function resetFilter() {
        setZeitOption(option.stichtag);
        setFilter(defaultFilter);
    }

    return visible === false ? null : (
        <div className="gridl tabsheet-content-margin">
            <EAFieldset id={id} caption="Filter">
                <div className="gridl" style={{gridTemplateColumns: "1fr 1fr 1.7fr 2.3fr", alignItems: "center"}}>
                    {formFactory.renderAutocomplete(
                        attr.abwasseranlage,
                        id + "-" + attr.abwasseranlage,
                        "Abwasseranlage",
                        anlagenOpts.data,
                        (o) => {
                            return `${o.bezeichnung}(${o.anlagenId})`;
                        },
                        "id",
                        anlagenOpts.loading,
                        false,
                        false,
                        {style: {gridColumn: "span 2"}},
                        [
                            {
                                id: attr.messstelle,
                                calcDependentVal: (newSelected, proposedState) => {
                                    return !newSelected || newSelected?.id === proposedState.messstelle?.abwasseranlage.id
                                        ? proposedState.messstelle
                                        : null;
                                },
                            },
                        ]
                    )}
                    {formFactory.renderAutocomplete(
                        attr.messstelle,
                        id + "-" + attr.messstelle,
                        "Messstelle",
                        messstellenOpts.data?.filter(
                            (messtelle) => !filter.abwasseranlage || messtelle.abwasseranlage.id === filter.abwasseranlage?.id
                        ),
                        (o) => {
                            return `${o.bezeichnung}(${o.messstellenId})`;
                        },
                        "id",
                        messstellenOpts.loading,
                        false
                    )}
                    {formFactory.renderAutocomplete(
                        attr.parameter,
                        id + "-" + attr.parameter,
                        "Parameter",
                        parameterOpts.data,
                        "parameterName",
                        "parameterNr",
                        parameterOpts.loading,
                        false
                    )}
                    <RadioGroup
                        aria-label="anonymous"
                        name="anonymous"
                        value={zeitOption}
                        onChange={(event) => setZeitOption(event.target.value)}
                        row
                        style={{gridColumn: "span 3"}}
                    >
                        <FormControlLabel value={option.alle} control={<Radio/>} label="alle Zeiträume"/>
                        <FormControlLabel value={option.zeitraum} control={<Radio/>} label="im Zeitraum"/>
                        <FormControlLabel value={option.stichtag} control={<Radio/>} label="zum Stichtag"/>
                    </RadioGroup>
                    {zeitOption === option.stichtag &&
                        formFactory.renderDatePicker(attr.stichtag, id + "-" + attr.stichtag, "Stichtag", false, {
                            style: {gridColumn: 3, maxWidth: 144},
                        })}
                    {zeitOption === option.zeitraum &&
                        formFactory.renderDatePicker(attr.gueltigAb, id + "-" + attr.gueltigAb, "von", false, {
                            style: {gridColumn: 1},
                        })}
                    {zeitOption === option.zeitraum &&
                        formFactory.renderDatePicker(attr.gueltigBis, id + "-" + attr.gueltigBis, "bis", false, {
                            style: {gridColumn: 2},
                        })}
                    <EAButtonBar style={{gridColumn: 4, gridRow: 3}}>
                        <EAButton id={id + "-reset"} label="Zurücksetzen" onClick={() => resetFilter()}/>
                        <EAButton
                            id={id + "-submit"}
                            label="Suchen"
                            onClick={() => reFetch()}
                            loading={anforderungenLoading}
                            disabled={anforderungenLoading}
                            color="primary"
                        />
                    </EAButtonBar>
                </div>
            </EAFieldset>
            <EATable
                id={id}
                loading={anforderungenLoading}
                initialSortColumn="betrieb"
                columns={columnsSlize}
                data={data}
                style={{width: "auto"}}
                className={"noHorizontalPadding"}
            />
        </div>
    );
}

export default AnforderungenSuche;
